import {
  BufferAttribute,
  BufferGeometry,
  DynamicDrawUsage,
  type InterleavedBufferAttribute,
  LineSegments,
  ShaderMaterial,
} from "three";
import { LineMaterial } from "three/addons/lines/LineMaterial.js";
import { LineSegments2 } from "three/addons/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/addons/lines/LineSegmentsGeometry.js";
import type { Elements } from "../../data/schema";
import type { BodyTable } from "../../sim/body-table";
import { orbitStateAt } from "../../sim/elements";
import { createOrbitState, positionFromState } from "../../sim/kepler";
import { wrapTwoPi } from "../../sim/time";
import type { FrameData } from "../frame";
import { nearestSegmentD2 } from "../picking";
import { projectAll } from "../project";
import type { ScaleMapper } from "../scale-mapper";
import {
  ARC_CAPACITY,
  type Arc,
  coarseGapPx,
  createArc,
  forEachRun,
  mapOffset,
  pixelsPerUnit,
  refineArc,
  selectRefinements,
} from "./orbit-refine";
import type { Tier } from "./tier";

const ORBIT_SEGMENTS = 128;
const ARC_SEGMENTS = ARC_CAPACITY - 1;
const MAX_REFINED = 8; // orbits refined per frame
const REBUILD_DAYS = 1; // orbits with secular rates are rebuilt once the clock moves this far
const PLANET_ALPHA = 0.35;
const MOON_ALPHA = 0.5;
const POINT_ALPHA = 0.15;
const FADE_RADII = 12; // moon orbits fade out once the camera is this many orbit radii from the parent
const PICK_MIN_ALPHA = 0.05; // fainter orbits are not hoverable
const HIGHLIGHT_WIDTH_PX = 3;
const ORBIT_RGB = [0.55, 0.65, 0.85] as const;

const state = createOrbitState();

/** One closed loop of `segments` points (km, relative to the parent), evenly spaced in eccentric anomaly. */
export function orbitPolyline(el: Elements, jd: number, segments: number, out: Float64Array): void {
  orbitStateAt(el, jd, state);
  for (let k = 0; k < segments; k++)
    positionFromState(state, (2 * Math.PI * k) / segments, out, 3 * k);
}

export function orbitAlpha(
  cameraToParent: number,
  orbitRadiusScene: number,
  isPlanet: boolean,
  isPoint = false,
): number {
  if (isPlanet) return PLANET_ALPHA;
  const t = 1 - cameraToParent / (FADE_RADII * orbitRadiusScene);
  const fade = MOON_ALPHA * Math.min(Math.max(t, 0), 1);
  return isPoint ? Math.min(fade, POINT_ALPHA) : fade;
}

const vertex = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_vertex>
  attribute float aAlpha;
  varying float vAlpha;
  void main() {
    vAlpha = aAlpha;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    #include <logdepthbuf_vertex>
  }`;

const fragment = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_fragment>
  varying float vAlpha;
  void main() {
    if (vAlpha <= 0.001) discard;
    gl_FragColor = vec4(${ORBIT_RGB.join(", ")}, vAlpha);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
    #include <logdepthbuf_fragment>
  }`;

interface Orbit {
  index: number;
  parent: number;
  isPlanet: boolean;
  isPoint: boolean;
  a: number;
  el: Elements;
  /** Secular rates move the elements, so the loop is rebuilt as the clock runs. */
  drifts: boolean;
  builtJd: number;
  local: Float64Array;
}

function lineBuffers(vertices: number): {
  geometry: BufferGeometry;
  position: BufferAttribute;
  alpha: BufferAttribute;
} {
  const position = new BufferAttribute(new Float32Array(3 * vertices), 3).setUsage(
    DynamicDrawUsage,
  );
  const alpha = new BufferAttribute(new Float32Array(vertices), 1).setUsage(DynamicDrawUsage);
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", position);
  geometry.setAttribute("aAlpha", alpha);
  return { geometry, position, alpha };
}

const bodyRel = new Float64Array(3);
const camRel = new Float64Array(3);

export class OrbitsTier implements Tier {
  readonly object: LineSegments;
  private readonly orbits: Orbit[] = [];
  private readonly localScene: Float32Array; // mapped loop per orbit, 3*ORBIT_SEGMENTS each
  private readonly position: BufferAttribute;
  private readonly alpha: BufferAttribute;
  /** Alpha each orbit was last drawn with. */
  private readonly drawnAlpha: Float32Array;
  /** Orbit slot of each body, -1 for bodies without an orbit. */
  private readonly orbitOf: Int32Array;
  private readonly highlight: LineSegments2;
  private readonly highlightMaterial: LineMaterial;
  private readonly projected: Float32Array;
  /** Dense arcs around the bodies whose coarse loop is visibly off, one slot of ARC_SEGMENTS each. */
  private readonly detail: LineSegments;
  private readonly detailPosition: BufferAttribute;
  private readonly detailAlpha: BufferAttribute;
  private readonly arcs: Arc[];
  private readonly selected: Int32Array;
  private readonly gaps: Float32Array;
  /** Arc slot of each orbit in the frame last drawn, -1 when unrefined. */
  private readonly slotOf: Int32Array;
  private readonly projectedArc = new Float32Array(3 * 2 * ARC_SEGMENTS);
  private mappedVersion = -1;
  hoverBody = -1;

  constructor(
    table: BodyTable,
    private readonly mapper: ScaleMapper,
    maxRefined = MAX_REFINED,
  ) {
    table.bodies.forEach((b, index) => {
      if (!b.orbit) return;
      const local = new Float64Array(3 * ORBIT_SEGMENTS);
      orbitPolyline(b.orbit, b.orbit.epochJd, ORBIT_SEGMENTS, local);
      this.orbits.push({
        index,
        parent: table.parentIndex[index],
        isPlanet: b.kind !== "moon",
        isPoint: b.tier === "point",
        a: b.orbit.a,
        el: b.orbit,
        drifts: b.orbit.rates !== undefined,
        builtJd: b.orbit.epochJd,
        local,
      });
    });
    const vertsPerOrbit = 2 * ORBIT_SEGMENTS;
    this.localScene = new Float32Array(3 * ORBIT_SEGMENTS * this.orbits.length);
    const coarse = lineBuffers(vertsPerOrbit * this.orbits.length);
    this.position = coarse.position;
    this.alpha = coarse.alpha;
    const material = new ShaderMaterial({
      vertexShader: vertex,
      fragmentShader: fragment,
      transparent: true,
      depthWrite: false,
    });
    this.object = new LineSegments(coarse.geometry, material);
    this.object.frustumCulled = false;
    this.drawnAlpha = new Float32Array(this.orbits.length);
    this.orbitOf = new Int32Array(table.count).fill(-1);
    this.orbits.forEach((orbit, n) => (this.orbitOf[orbit.index] = n));
    this.projected = new Float32Array(3 * vertsPerOrbit);

    const detail = lineBuffers(2 * ARC_SEGMENTS * maxRefined);
    this.detailPosition = detail.position;
    this.detailAlpha = detail.alpha;
    this.detail = new LineSegments(detail.geometry, material);
    this.detail.name = "orbit-detail";
    this.detail.frustumCulled = false;
    this.object.add(this.detail);
    this.arcs = Array.from({ length: maxRefined }, createArc);
    this.selected = new Int32Array(maxRefined);
    this.gaps = new Float32Array(this.orbits.length);
    this.slotOf = new Int32Array(this.orbits.length).fill(-1);

    // plain lines are always one device pixel wide; the hovered orbit is redrawn as a fat line
    const highlightGeometry = new LineSegmentsGeometry();
    highlightGeometry.setPositions(new Float32Array(6 * (ORBIT_SEGMENTS + ARC_SEGMENTS)));
    this.highlightMaterial = new LineMaterial({
      linewidth: HIGHLIGHT_WIDTH_PX,
      transparent: true,
      depthWrite: false,
    });
    this.highlightMaterial.color.setRGB(...ORBIT_RGB);
    this.highlight = new LineSegments2(highlightGeometry, this.highlightMaterial);
    this.highlight.name = "orbit-highlight";
    this.highlight.frustumCulled = false;
    this.highlight.visible = false;
    // a child, so hiding the orbits hides the highlight too
    this.object.add(this.highlight);
  }

  /** Viewport size in CSS pixels, which sets the highlight's line width. */
  setResolution(width: number, height: number): void {
    this.highlightMaterial.resolution.set(width, height);
  }

  /**
   * Body whose drawn orbit passes within `maxPx` of (x, y), nearest first, or -1.
   * `m` maps the tier's positions to clip space, as for the frame they were last drawn in.
   */
  pick(
    m: ArrayLike<number>,
    width: number,
    height: number,
    x: number,
    y: number,
    maxPx: number,
  ): number {
    if (!this.object.visible) return -1;
    let best = -1,
      bestD2 = maxPx * maxPx;
    this.orbits.forEach((orbit, n) => {
      if (this.drawnAlpha[n] < PICK_MIN_ALPHA) return;
      const d2 = this.drawnD2(n, m, width, height, x, y);
      if (d2 <= bestD2) {
        best = orbit.index;
        bestD2 = d2;
      }
    });
    return best;
  }

  /** Squared pixel distance from (x, y) to orbit n as drawn: its visible coarse segments and its arc. */
  private drawnD2(
    n: number,
    m: ArrayLike<number>,
    width: number,
    height: number,
    x: number,
    y: number,
  ): number {
    const verts = 2 * ORBIT_SEGMENTS;
    const pos = this.position.array as Float32Array;
    projectAll(pos.subarray(3 * verts * n), verts, m, width, height, this.projected);
    const slot = this.slotOf[n];
    if (slot < 0) return nearestSegmentD2(this.projected, ORBIT_SEGMENTS, x, y);
    const arc = this.arcs[slot];
    let best = Infinity;
    forEachRun(arc.kHi, ORBIT_SEGMENTS - (arc.kHi - arc.kLo), ORBIT_SEGMENTS, (k, len) => {
      best = Math.min(best, nearestSegmentD2(this.projected.subarray(6 * k), len, x, y));
    });
    const segs = arc.count - 1;
    const detail = this.detailPosition.array as Float32Array;
    projectAll(
      detail.subarray(6 * ARC_SEGMENTS * slot),
      2 * segs,
      m,
      width,
      height,
      this.projectedArc,
    );
    return Math.min(best, nearestSegmentD2(this.projectedArc, segs, x, y));
  }

  private remapOrbit(n: number): void {
    const { local } = this.orbits[n];
    const base = 3 * ORBIT_SEGMENTS * n;
    for (let k = 0; k < ORBIT_SEGMENTS; k++)
      mapOffset(
        this.mapper,
        local[3 * k],
        local[3 * k + 1],
        local[3 * k + 2],
        this.localScene,
        base + 3 * k,
      );
  }

  private remap(): void {
    this.orbits.forEach((_, n) => this.remapOrbit(n));
    this.mappedVersion = this.mapper.version;
  }

  private rebuildDrifting(jd: number): void {
    this.orbits.forEach((orbit, n) => {
      if (!orbit.drifts || Math.abs(jd - orbit.builtJd) <= REBUILD_DAYS) return;
      orbitPolyline(orbit.el, jd, ORBIT_SEGMENTS, orbit.local);
      orbit.builtJd = jd;
      this.remapOrbit(n);
    });
  }

  update(frame: FrameData): void {
    if (this.mappedVersion !== this.mapper.version) this.remap();
    this.rebuildDrifting(frame.jd);
    const { positions, cameraPosition, shown } = frame;
    const pos = this.position.array as Float32Array,
      alpha = this.alpha.array as Float32Array;
    this.orbits.forEach((orbit, n) => {
      const po = 3 * orbit.parent;
      const px = positions[po],
        py = positions[po + 1],
        pz = positions[po + 2];
      const camDist = Math.hypot(
        px - cameraPosition.x,
        py - cameraPosition.y,
        pz - cameraPosition.z,
      );
      const a =
        shown[orbit.index] === 0
          ? 0
          : orbit.index === this.hoverBody
            ? 1
            : orbitAlpha(camDist, this.mapper.mapDistance(orbit.a), orbit.isPlanet, orbit.isPoint);
      this.drawnAlpha[n] = a;
      const localBase = 3 * ORBIT_SEGMENTS * n;
      const vertBase = 2 * ORBIT_SEGMENTS * n;
      for (let k = 0; k < ORBIT_SEGMENTS; k++) {
        const k2 = (k + 1) % ORBIT_SEGMENTS;
        const v = vertBase + 2 * k;
        pos[3 * v] = px + this.localScene[localBase + 3 * k];
        pos[3 * v + 1] = py + this.localScene[localBase + 3 * k + 1];
        pos[3 * v + 2] = pz + this.localScene[localBase + 3 * k + 2];
        pos[3 * v + 3] = px + this.localScene[localBase + 3 * k2];
        pos[3 * v + 4] = py + this.localScene[localBase + 3 * k2 + 1];
        pos[3 * v + 5] = pz + this.localScene[localBase + 3 * k2 + 2];
        alpha[v] = a;
        alpha[v + 1] = a;
      }
    });
    this.refine(frame, alpha);
    this.position.needsUpdate = true;
    this.alpha.needsUpdate = true;
    this.updateHighlight(pos);
  }

  /** Writes body and camera positions relative to orbit n's parent into bodyRel and camRel. */
  private relative(frame: FrameData, orbit: Orbit): void {
    const { positions, cameraPosition: cam } = frame;
    const o = 3 * orbit.index,
      po = 3 * orbit.parent;
    bodyRel[0] = positions[o] - positions[po];
    bodyRel[1] = positions[o + 1] - positions[po + 1];
    bodyRel[2] = positions[o + 2] - positions[po + 2];
    camRel[0] = cam.x - positions[po];
    camRel[1] = cam.y - positions[po + 1];
    camRel[2] = cam.z - positions[po + 2];
  }

  /** Splices a dense arc around each body whose coarse loop is visibly off into its orbit. */
  private refine(frame: FrameData, alpha: Float32Array): void {
    const ppu = pixelsPerUnit(frame.viewportHeight, frame.fovRad);
    const step = (2 * Math.PI) / ORBIT_SEGMENTS;
    this.slotOf.fill(-1);
    this.orbits.forEach((orbit, n) => {
      this.gaps[n] = 0;
      if (this.drawnAlpha[n] === 0) return;
      this.relative(frame, orbit);
      const k = Math.min(
        Math.floor(wrapTwoPi(frame.anomalies[orbit.index]) / step),
        ORBIT_SEGMENTS - 1,
      );
      const base = ORBIT_SEGMENTS * n;
      const k2 = base + ((k + 1) % ORBIT_SEGMENTS);
      this.gaps[n] = coarseGapPx(bodyRel, this.localScene, base + k, k2, camRel, ppu);
    });
    const count = selectRefinements(this.gaps, this.selected);
    const detailPos = this.detailPosition.array as Float32Array,
      detailAlpha = this.detailAlpha.array as Float32Array;
    for (let slot = 0; slot < count; slot++) {
      const n = this.selected[slot],
        orbit = this.orbits[n],
        arc = this.arcs[slot];
      this.relative(frame, orbit);
      orbitStateAt(orbit.el, frame.jd, state);
      const coarse = this.localScene.subarray(3 * ORBIT_SEGMENTS * n, 3 * ORBIT_SEGMENTS * (n + 1));
      refineArc(
        state,
        wrapTwoPi(frame.anomalies[orbit.index]),
        this.mapper,
        coarse,
        ORBIT_SEGMENTS,
        bodyRel,
        camRel,
        ppu,
        arc,
      );
      this.slotOf[n] = slot;
      const vertBase = 2 * ORBIT_SEGMENTS * n;
      forEachRun(arc.kLo, arc.kHi - arc.kLo, ORBIT_SEGMENTS, (k, len) =>
        alpha.fill(0, vertBase + 2 * k, vertBase + 2 * (k + len)),
      );
      this.writeArc(frame, orbit, arc, slot, detailPos, detailAlpha, this.drawnAlpha[n]);
    }
    detailAlpha.fill(0, 2 * ARC_SEGMENTS * count);
    // only the slots in use are drawn, so an idle pool costs no draw and no upload
    this.detail.geometry.setDrawRange(0, 2 * ARC_SEGMENTS * count);
    if (count === 0) return;
    this.detailPosition.needsUpdate = true;
    this.detailAlpha.needsUpdate = true;
  }

  private writeArc(
    frame: FrameData,
    orbit: Orbit,
    arc: Arc,
    slot: number,
    pos: Float32Array,
    alpha: Float32Array,
    a: number,
  ): void {
    const { positions } = frame;
    const po = 3 * orbit.parent;
    const px = positions[po],
      py = positions[po + 1],
      pz = positions[po + 2];
    const src = arc.positions;
    const base = 2 * ARC_SEGMENTS * slot;
    for (let j = 0; j + 1 < arc.count; j++) {
      const v = base + 2 * j;
      pos[3 * v] = px + src[3 * j];
      pos[3 * v + 1] = py + src[3 * j + 1];
      pos[3 * v + 2] = pz + src[3 * j + 2];
      pos[3 * v + 3] = px + src[3 * j + 3];
      pos[3 * v + 4] = py + src[3 * j + 4];
      pos[3 * v + 5] = pz + src[3 * j + 5];
      alpha[v] = a;
      alpha[v + 1] = a;
    }
    alpha.fill(0, base + 2 * (arc.count - 1), base + 2 * ARC_SEGMENTS);
  }

  private updateHighlight(pos: Float32Array): void {
    const n = this.hoverBody >= 0 ? this.orbitOf[this.hoverBody] : -1;
    this.highlight.visible = n >= 0 && this.drawnAlpha[n] > 0;
    if (!this.highlight.visible) return;
    // LineSegmentsGeometry stores segments as (start xyz, end xyz), the same layout as `pos`
    const start = this.highlight.geometry.attributes.instanceStart as InterleavedBufferAttribute;
    const dst = start.data.array as Float32Array;
    const size = 6 * ORBIT_SEGMENTS;
    const src = pos.subarray(size * n, size * (n + 1));
    const slot = this.slotOf[n];
    let used = 0;
    if (slot < 0) {
      dst.set(src);
      used = ORBIT_SEGMENTS;
    } else {
      // only what is drawn: the visible coarse segments, then the arc
      const arc = this.arcs[slot];
      forEachRun(arc.kHi, ORBIT_SEGMENTS - (arc.kHi - arc.kLo), ORBIT_SEGMENTS, (k, len) => {
        dst.set(src.subarray(6 * k, 6 * (k + len)), 6 * used);
        used += len;
      });
      const segs = arc.count - 1;
      const detail = this.detailPosition.array as Float32Array;
      dst.set(detail.subarray(6 * ARC_SEGMENTS * slot, 6 * (ARC_SEGMENTS * slot + segs)), 6 * used);
      used += segs;
    }
    this.highlight.geometry.instanceCount = used;
    start.data.needsUpdate = true;
  }
}
